import React, { useState } from 'react'
import { Library, Plus, FileText, Clapperboard, Mic, Rss } from 'lucide-react'
import ProjectBar from '../components/ProjectBar'
import DocumentsPage from './DocumentsPage'
import ImportPage from './ImportPage'
import TranscriptsLibrary from '../components/TranscriptsLibrary'
import FeedsPanel from '../components/FeedsPanel'

const MODES = [
  ['library', 'Library', Library],
  ['add', 'Add', Plus],
  ['transcripts', 'Transcripts', Mic],
  ['feeds', 'Feeds', Rss],
]
const ADD_KINDS = [
  ['files', 'Files & folders', FileText],
  ['media', 'Media & URLs', Clapperboard],
]

export default function SourcesPage() {
  const [mode, setMode] = useState('library')
  const [addKind, setAddKind] = useState('files')
  // One shared project across all Sources modes — picked once in the bar below.
  const [project, setProject] = useState('')
  // "Full pipeline…" from Transcripts: hand a channel video to the Media wizard.
  const [wizardPrefill, setWizardPrefill] = useState(null)
  const openFullPipeline = (video) => {
    setWizardPrefill(video)
    setMode('add')
    setAddKind('media')
  }

  return (
    <div className="space-y-6">
      <header>
        <div className="text-[11px] uppercase tracking-[0.22em] text-gold-deep dark:text-gold mb-2">The Archive</div>
        <h1 className="font-display text-4xl text-ink dark:text-mist">Sources</h1>
        <p className="mt-2 text-ink-soft dark:text-mist/60">
          Everything Veralis can search — browse your library, or add new sources.
        </p>
      </header>

      {/* Mode toggles */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="inline-flex rounded-lg border border-parchment-border dark:border-midnight-600 p-0.5">
          {MODES.map(([m, label, Icon]) => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition ${
                mode === m ? 'bg-gold/15 text-gold-deep dark:text-gold' : 'text-ink-soft dark:text-mist/60'
              }`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>
        {mode === 'add' && (
          <div className="inline-flex rounded-lg border border-parchment-border dark:border-midnight-600 p-0.5">
            {ADD_KINDS.map(([k, label, Icon]) => (
              <button
                key={k}
                type="button"
                onClick={() => setAddKind(k)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition ${
                  addKind === k ? 'bg-spark/15 text-spark-600 dark:text-spark' : 'text-ink-soft dark:text-mist/60'
                }`}
              >
                <Icon className="w-4 h-4" />
                {label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Shared project bar — same first row across Library / Files / Media / Feeds.
          Hidden for Transcripts, which is channel-oriented, not project-scoped. */}
      {mode !== 'transcripts' && <ProjectBar project={project} onProjectChange={setProject} />}

      {/* Keep modes mounted so state/progress (uploads, running imports) survive switches. */}
      <div style={{ display: mode === 'library' ? 'block' : 'none' }}>
        <DocumentsPage embedded view="library" project={project} />
      </div>
      <div style={{ display: mode === 'add' && addKind === 'files' ? 'block' : 'none' }}>
        <DocumentsPage embedded view="add" project={project} />
      </div>
      <div style={{ display: mode === 'add' && addKind === 'media' ? 'block' : 'none' }}>
        <ImportPage embedded project={project} prefill={wizardPrefill} />
      </div>
      <div style={{ display: mode === 'transcripts' ? 'block' : 'none' }}>
        {/* project powers the per-video "Index into <project>" action */}
        <TranscriptsLibrary project={project} onFullPipeline={openFullPipeline} />
      </div>
      <div style={{ display: mode === 'feeds' ? 'block' : 'none' }}>
        <FeedsPanel project={project} />
      </div>
    </div>
  )
}
